import styled from "@emotion/styled";
import { Box, systemWidth, useTheme } from "@kaltura/mediaspace-shared-styled";
import { Button, Typography } from "@kaltura/ds-react-components";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { contrastTextShadow } from "@kaltura/mediaspace-shared-ui";

export interface BannerCtaProps {
    headerText?: string;
    descriptionText?: string;
    bannerImage?: string;
    buttonText?: string;
    buttonUrl?: string;
    openLinkInNewTab?: boolean;
}

export const StyledBanner = styled(Box)(({ theme }) => ({
    "&": systemWidth({ theme }),
    minHeight: theme.typography.pxToRem(247),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "center",
    borderRadius: theme.kaltura.shape.roundness2,
    padding: theme.spacing(7, 3),
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(5, 3),
    },
}));

export const StyledTitle = styled(
    Typography,
    {
        shouldForwardProp: (propName) => propName !== "hasBannerImage",
    }
)<{ hasBannerImage?: boolean }>(({ hasBannerImage }) => ({
    marginTop: 0,
    textShadow: hasBannerImage ? contrastTextShadow : "none",
}));

export const StyledTypography = styled(
    Typography,
    {
        shouldForwardProp: (propName) => propName !== "hasBannerImage",
    }
)<{ hasBannerImage?: boolean }>(({ hasBannerImage }) => ({
    textShadow: hasBannerImage ? contrastTextShadow : "none",
}));

export const StyledCtaButton = styled(Button<"a">)(({ theme }) => ({
    marginTop: theme.spacing(3),
    minWidth: theme.typography.pxToRem(200),
}));

/**
 * Banner CTA component - banner with background image, title, description and CTA button - all are optional
 */
export function BannerCta(props: BannerCtaProps) {
    const {
        headerText = "",
        descriptionText = "",
        bannerImage = "",
        buttonText = "",
        buttonUrl = "",
        openLinkInNewTab = false,
    } = props;
    const theme = useTheme();
    const isXsScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
    const sendButtonAnalytics = useButtonAnalytics();

    return (
        <StyledBanner
            className={"kms-ds-homepage-component-banner-cta-container"}
            style={{
                background: bannerImage
                    ? `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.60) 100%), url(${bannerImage}) lightgray 50% / cover no-repeat`
                    : theme.kaltura.palette.tone7,
                color: bannerImage ? theme.palette.common.white : theme.kaltura.palette.tone1,
            }}
        >
            {headerText && (
                <StyledTitle
                    className={"kms-ds-homepage-component-banner-cta-title"}
                    variant={isXsScreen ? "h3" : "h2"}
                    hasBannerImage={Boolean(bannerImage)}
                >
                    {headerText}
                </StyledTitle>
            )}
            {descriptionText && (
                <StyledTypography
                    className={"kms-ds-homepage-component-banner-cta-description"}
                    variant={"body1"}
                    hasBannerImage={Boolean(bannerImage)}
                >
                    {descriptionText}
                </StyledTypography>
            )}
            {buttonText && buttonUrl && (
                <StyledCtaButton
                    component={"a"}
                    size={"large"}
                    className={"kms-ds-homepage-component-banner-cta-button"}
                    href={buttonUrl}
                    onClick={() => sendButtonAnalytics("Custom page - Banner CTA link", ButtonClickAnalyticsType.LINK)}
                    target={openLinkInNewTab ? "_blank" : "_self"}
                >
                    {buttonText}
                </StyledCtaButton>
            )}
        </StyledBanner>
    );
}

export default BannerCta;
